import type { z } from 'zod'
import type { LowerHex, TransactionDecoded } from '../../../../types'
import type { mathValueSchema } from './schema'
import { getSimpleValue } from './simpleValue'

export const getMathValue = ({
  schema,
  transaction,
  relatedAddresses,
}: {
  readonly schema: z.infer<typeof mathValueSchema>
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): bigint => {
  const firstValue = schema.values[0]
  let current = BigInt(
    getSimpleValue({ schema: firstValue, transaction, relatedAddresses }),
  )
  let operator: '+' | '-' | '*' | '/' | undefined
  let args: bigint[] = []
  for (const value of schema.values.slice(1)) {
    if (value === '+') {
      if (operator !== undefined) {
        current = calculate(operator, current, args)
        args = []
      }
      operator = '+'
      continue
    }
    if (value === '-') {
      if (operator !== undefined) {
        current = calculate(operator, current, args)
        args = []
      }
      operator = '-'
      continue
    }
    if (value === '*') {
      if (operator !== undefined) {
        current = calculate(operator, current, args)
        args = []
      }
      operator = '*'
      continue
    }
    if (value === '/') {
      if (operator !== undefined) {
        current = calculate(operator, current, args)
        args = []
      }
      operator = '/'
      continue
    }
    args.push(
      BigInt(getSimpleValue({ schema: value, transaction, relatedAddresses })),
    )
  }
  if (operator !== undefined) {
    current = calculate(operator, current, args)
  }
  return current
}

const calculate = (
  operator: '+' | '-' | '*' | '/',
  current: bigint,
  args: bigint[],
): bigint => {
  let newValue = current
  switch (operator) {
    case '+': {
      for (const arg of args) {
        newValue += arg
      }
      return newValue
    }
    case '-': {
      for (const arg of args) {
        newValue -= arg
      }
      return newValue
    }
    case '*': {
      for (const arg of args) {
        newValue *= arg
      }
      return newValue
    }
    case '/': {
      for (const arg of args) {
        newValue /= arg
      }
      return newValue
    }
    default: {
      throw new Error(`Unknown operator: ${operator satisfies never}`)
    }
  }
}
